import React, { useState } from 'react';
import { Calculator, RotateCcw, Info, CheckCircle2, Circle } from 'lucide-react';
import { IPI_FIELDS, IPI_STAGES, IPI_ECOG, calculateIpi } from '../utils/ipi';

/**
 * IpiCalculator - standalone International Prognostic Index calculator for
 * Sales users (client feedback #13, Oct 2026). Opened from the profile menu
 * (/ipi-calculator; Sales and Superadmin). No patient name, identifier or case:
 * the five values live only in this page's state and are never sent or saved.
 * The result updates as the fields change; Reset clears them. Scoring:
 * utils/ipi.js (same rules as the NHL planner).
 */

const EMPTY = { age: '', stage: '', ldh: '', ecog: '', extranodal: '' };

const HINTS = {
  age: '1 point if older than 60',
  stage: '1 point for stage III or IV',
  ldh: 'Multiple of the upper limit of normal, e.g. 1.5 · 1 point if above 1 × ULN',
  ecog: '1 point for ECOG 2 or more',
  extranodal: 'Number of involved extranodal sites · 1 point if more than 1',
};

export default function IpiCalculator() {
  const [values, setValues] = useState(EMPTY);
  const result = calculateIpi(values);
  const set = (key) => (e) => setValues((v) => ({ ...v, [key]: e.target.value }));
  const field = (key) => IPI_FIELDS.find((f) => f.key === key);

  const inputCls = (key) =>
    `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${
      result.errors[key] ? 'border-red-400' : 'border-gray-300'
    }`;

  const Label = ({ k }) => (
    <label htmlFor={`ipi-${k}`} className="block text-sm font-medium text-gray-700 mb-1.5">
      {field(k).label}{field(k).unit ? <span className="text-gray-400 font-normal"> ({field(k).unit})</span> : null}
    </label>
  );
  const Help = ({ k }) =>
    result.errors[k]
      ? <p id={`ipi-${k}-error`} className="text-xs text-red-600 mt-1" role="alert">{result.errors[k]}</p>
      : <p id={`ipi-${k}-hint`} className="text-xs text-gray-400 mt-1">{HINTS[k]}</p>;
  const aria = (k) => ({
    'aria-invalid': !!result.errors[k],
    'aria-describedby': result.errors[k] ? `ipi-${k}-error` : `ipi-${k}-hint`,
  });

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      {/* Header */}
      <div className="flex items-center space-x-3 mb-6">
        <Calculator className="w-7 h-7 text-emerald-600 flex-shrink-0" aria-hidden="true" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">IPI Calculator</h1>
          <p className="text-sm text-gray-500">International Prognostic Index - enter the five parameters to see the score and risk category.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Inputs */}
        <section className="lg:col-span-3 bg-white rounded-xl border border-gray-200 shadow-sm p-5 sm:p-6" aria-label="IPI parameters">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-gray-900">Parameters</h2>
            <button
              type="button"
              onClick={() => setValues(EMPTY)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
            >
              <RotateCcw className="w-4 h-4" aria-hidden="true" /> Reset
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
            <div>
              <Label k="age" />
              <input id="ipi-age" type="number" inputMode="numeric" min="0" max="120" step="1" placeholder="e.g. 64"
                value={values.age} onChange={set('age')} className={inputCls('age')} {...aria('age')} />
              <Help k="age" />
            </div>
            <div>
              <Label k="stage" />
              <select id="ipi-stage" value={values.stage} onChange={set('stage')} className={inputCls('stage')} {...aria('stage')}>
                <option value="">Select stage</option>
                {IPI_STAGES.map((s) => <option key={s} value={s}>Stage {s}</option>)}
              </select>
              <Help k="stage" />
            </div>
            <div>
              <Label k="ldh" />
              <input id="ipi-ldh" type="number" inputMode="decimal" min="0" step="0.1" placeholder="e.g. 1.5"
                value={values.ldh} onChange={set('ldh')} className={inputCls('ldh')} {...aria('ldh')} />
              <Help k="ldh" />
            </div>
            <div>
              <Label k="ecog" />
              <select id="ipi-ecog" value={values.ecog} onChange={set('ecog')} className={inputCls('ecog')} {...aria('ecog')}>
                <option value="">Select ECOG</option>
                {IPI_ECOG.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <Help k="ecog" />
            </div>
            <div className="sm:col-span-2">
              <Label k="extranodal" />
              <input id="ipi-extranodal" type="number" inputMode="numeric" min="0" step="1" placeholder="e.g. 1"
                value={values.extranodal} onChange={set('extranodal')} className={inputCls('extranodal')} {...aria('extranodal')} />
              <Help k="extranodal" />
            </div>
          </div>
        </section>

        {/* Result */}
        <section className="lg:col-span-2 bg-white rounded-xl border border-gray-200 shadow-sm p-5 sm:p-6" aria-label="IPI result" aria-live="polite">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Result</h2>
          {!result.complete ? (
            <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-6 text-center">
              <p className="text-sm text-gray-600" id="ipi-incomplete">Complete all fields to calculate IPI</p>
            </div>
          ) : (
            <>
              <div className="flex items-end justify-between gap-3 mb-4">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total IPI score</p>
                  <p className="text-4xl font-bold text-gray-900 mt-1" id="ipi-score">{result.score}<span className="text-lg font-medium text-gray-400"> / 5</span></p>
                </div>
                <span
                  id="ipi-risk"
                  className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold ${
                    result.risk === 'High Risk' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                  }`}
                >
                  {result.risk}
                </span>
              </div>
              <p className="text-xs text-gray-400 mb-3">Low Risk 0–2 · High Risk 3–5, as in the NHL planner.</p>
              <ul className="divide-y divide-gray-100 border-t border-gray-100" id="ipi-breakdown">
                {result.breakdown.map((b) => (
                  <li key={b.key} className="flex items-center justify-between gap-3 py-2.5">
                    <div className="flex items-start gap-2 min-w-0">
                      {b.point
                        ? <CheckCircle2 className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
                        : <Circle className="w-4 h-4 text-gray-300 mt-0.5 flex-shrink-0" aria-hidden="true" />}
                      <div className="min-w-0">
                        <p className="text-sm text-gray-800">{b.label}</p>
                        <p className="text-xs text-gray-400">{b.rule}</p>
                      </div>
                    </div>
                    <span className={`text-sm font-semibold flex-shrink-0 ${b.point ? 'text-red-600' : 'text-gray-400'}`}>
                      {b.point ? '+1' : '0'}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      </div>

      <div className="mt-6 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3">
        <Info className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
        <p className="text-sm text-amber-800">For reference and discussion only. Clinical interpretation and treatment decisions remain with the treating clinician.</p>
      </div>
    </div>
  );
}
